<template>
    <slide-over @submit="onSubmit" @close="close">
        <template #header>
            <h1 class="text-base font-semibold">
                {{ $t('professional.event_modal._title') }}
            </h1>
        </template>
        <template #footer>
            <div class="flex w-full gap-2">
                <button
                    v-if="isEdit"
                    class="btn btn--danger flex-none px-4"
                    @click.prevent="openEventDeleteDialog()"
                >
                    {{ $t('common.delete') }}
                </button>
                <button class="btn flex-1" :class="{ 'btn--disabled': invalid }">
                    {{ isEdit ? $t('common.save') : $t('calendar.create_event') }}
                </button>
            </div>
        </template>
        <template>
            <!-- Date & Time row -->
            <div class="mb-3 flex flex-wrap items-center gap-2">
                <div class="flex-1">
                    <InputDate v-model="fields.date" name="date" />
                </div>
                <div data-cy="startTime">
                    <InputTime
                        v-model="fields.startTime"
                        name="start_time"
                        :date="fields.date"
                        @input="applyDuration"
                    />
                </div>
                <select
                    v-model.number="selectedDuration"
                    class="h-10 rounded-lg border border-neutral-lighter px-2 text-sm outline-none focus:border-primary"
                    @change="applyDuration"
                >
                    <option :value="30">30 min</option>
                    <option :value="45">45 min</option>
                    <option :value="60">60 min</option>
                    <option :value="90">90 min</option>
                    <option :value="120">120 min</option>
                </select>
                <div data-cy="endTime" class="hidden sm:block">
                    <InputTime v-model="fields.endTime" name="end_time" :date="fields.date" />
                </div>
            </div>

            <!-- Recurrence update mode (edit recurring) -->
            <div v-if="isRecurring && isEdit" class="mb-3">
                <div class="mb-2">
                    {{ $t('professional.recurrence_update_modes.is_instance') }}
                </div>
                <div class="flex items-center">
                    <div class="mr-2 flex-none">
                        {{ $t('professional.recurrence_update_modes.when_saving_update') }}
                    </div>
                    <InputSelect
                        v-model="fields.recurrenceUpdateMode"
                        name="update_mode"
                        :options="recurrenceUpdateModes"
                    />
                </div>
            </div>

            <!-- Recurrence toggle -->
            <div
                v-if="!(isEdit && fields.recurrenceUpdateMode === 'this')"
                class="mb-3 flex items-center"
            >
                <InputCheckbox v-if="!isEdit" v-model="isRecurring" class="flex-none" />
                <div v-if="isRecurring" class="flex w-full flex-col gap-2">
                    <div class="flex items-center gap-2">
                        <select
                            v-model="recurrenceMode"
                            class="h-10 rounded-lg border border-neutral-lighter px-3 text-sm outline-none focus:border-primary"
                        >
                            <option value="count">
                                {{ $t('common.repeat') }}
                            </option>
                            <option value="until">Until date</option>
                        </select>
                        <template v-if="recurrenceMode === 'count'">
                            <div class="w-16">
                                <InputText
                                    v-model.number="fields.recurrence_rule.count"
                                    name="occurrences"
                                    type="number"
                                    min="1"
                                    max="52"
                                />
                            </div>
                            <span class="text-sm">
                                {{ $tc('common.occurrence', fields.recurrence_rule.count) }}
                            </span>
                        </template>
                        <template v-else>
                            <InputDate v-model="fields.recurrence_rule.until" name="until" />
                        </template>
                    </div>
                </div>
                <div v-else class="cursor-pointer" @click="isRecurring = true">
                    {{ $t('common.no_repeat') }}
                </div>
            </div>

            <!-- Voucher type -->
            <div class="mb-3">
                <label
                    class="mb-1 block text-xs font-medium uppercase tracking-wide text-neutral-darker"
                >
                    {{ $t('professional.event_modal.voucher_type') }}
                </label>
                <InputSelect
                    v-model="fields.selectedVoucherType"
                    name="voucher_type_id"
                    :options="voucherTypeSelectOptions"
                    :placeholder="$t('professional.event_modal.voucher_type')"
                />
                <p v-if="isTooManyClients" class="mt-1 text-xs text-error">
                    {{ $t('professional.event_modal.too_many_clients') }}
                </p>
            </div>

            <!-- Clients section -->
            <div class="mb-3">
                <label
                    class="mb-1 block text-xs font-medium uppercase tracking-wide text-neutral-darker"
                >
                    <span v-if="isEdit">
                        {{ $t('professional.event_modal.manage_registrations') }}
                    </span>
                    <span v-else>{{ $t('professional.event_modal.add_clients') }}</span>
                </label>
                <InputText
                    v-model="search"
                    class="mb-2"
                    icon="magnifying-glass"
                    :placeholder="$t('common.search')"
                    debounced
                    @input="onSearch"
                />
                <div
                    v-if="isLoading"
                    class="my-4 hidden items-center rounded-lg bg-neutral-lightest px-4 py-3 text-base md:flex"
                >
                    {{ $t('professional.calendar.loading_clients') }}
                </div>
                <InputSelectList
                    v-if="!isLoading"
                    v-slot="item"
                    v-model="fields.selectedClients"
                    name="clients"
                    :placeholder="$t('professional.event_modal.no_clients_in_clientbook')"
                    :options="clients"
                    label="full_name"
                    class="max-h-48"
                    @input="selectClient"
                    @load-more="fetchMoreClients"
                >
                    <div class="flex items-center gap-2 overflow-hidden text-sm">
                        <span
                            class="min-w-0 flex-1 truncate"
                            :class="{
                                'line-through opacity-50':
                                    cancelledClients && cancelledClients[item.id],
                            }"
                        >
                            {{ item.full_name || item.name }}
                        </span>
                        <span v-if="item.clients" class="flex-shrink-0 text-xs text-neutral-darker">
                            {{ item.clients.length }} clients
                        </span>
                        <span v-if="item.user" class="flex-shrink-0 text-xs text-neutral-darker">
                            {{ vouchersForClient(item.id) }}x
                            <Icon
                                :icon="['far', 'ticket']"
                                class="rotate-45 transform text-primary"
                            />
                        </span>
                    </div>
                </InputSelectList>
                <div class="mt-1 text-right text-xs text-neutral-darker">
                    {{ fields.allSelectedClients.length }} {{ $t('common.selected') }}
                </div>
            </div>

            <!-- Subscription availability -->
            <div v-if="bookingWarnings.length" class="mb-3">
                <div
                    v-for="(warning, idx) in bookingWarnings"
                    :key="idx"
                    class="mb-1 rounded-lg px-3 py-2 text-sm"
                    :class="
                        warning.canBook
                            ? 'bg-primary-lightest text-primary-darkest'
                            : 'bg-error-lighter text-error'
                    "
                >
                    {{ warning.message }}
                </div>
            </div>

            <!-- Invite clients (collapsed by default) -->
            <div class="mb-3">
                <button
                    v-if="!showInviteFields"
                    class="flex items-center gap-2 text-sm text-primary hover:text-primary-darker"
                    @click.prevent="showInviteFields = true"
                >
                    <Icon :icon="['far', 'user-plus']" />
                    {{ $t('professional.event_modal.invite_clients') }}
                </button>
                <div v-else>
                    <label
                        class="mb-1 block text-xs font-medium uppercase tracking-wide text-neutral-darker"
                    >
                        {{ $t('professional.event_modal.invite_clients') }}
                    </label>
                    <div class="mb-2">
                        <InputTag
                            v-model="fields.emails"
                            validate="email"
                            name="client_emails"
                            :label="$t('professional.event_modal.invite_by_email')"
                            :placeholder="$t('professional.event_modal.emails')"
                        />
                    </div>
                    <div>
                        <InputTag
                            v-model="fields.phones"
                            validate="phone"
                            name="client_phonenumbers"
                            :label="$t('professional.event_modal.invite_by_phone')"
                            :placeholder="
                                $t('professional.event_modal.phones') +
                                ' ' +
                                $t('common.country_code')
                            "
                        />
                    </div>
                </div>
            </div>

            <!-- Location -->
            <div class="mb-3">
                <InputText
                    v-model="fields.location"
                    name="location"
                    :placeholder="$t('professional.event_modal.location')"
                />
            </div>

            <!-- Notes -->
            <div class="mb-3">
                <InputTextArea
                    v-model="fields.notes"
                    name="notes"
                    rows="2"
                    :placeholder="$t('professional.event_modal.notes')"
                />
            </div>
        </template>
    </slide-over>
</template>

<script>
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import SlideOver from '@/components/modals/SlideOver';
import InputText from '@/components/input/InputText';
import InputTextArea from '@/components/input/InputTextArea';
import InputSelect from '@/components/input/InputSelect';
import InputSelectList from '@/components/input/InputSelectList';
import InputDate from '@/components/input/InputDate';
import InputTime from '@/components/input/InputTime';
import InputTag from '@/components/input/InputTag';
import InputCheckbox from '@/components/input/InputCheckbox';
import Avatar from '@/components/general/Avatar';
import EventDeleteDialog from '@/components/modals/EventDeleteDialog.vue';
import {
    CALENDAR_EVENTS_FETCH,
    CALENDAR_SLOTS_FETCH,
    CLIENTS_FETCH,
    CLIENTS_FETCH_NEXT_PAGE,
    CLIENTS_VOUCHERS_FETCH,
    GROUPS_FETCH,
    REQUEST,
    VALIDATION_CREATE_MESSAGE,
} from '@/constants';

import { CLIENT_BOOKING_AVAILABILITY } from '@/store/modules/subscriptions';
import moment from 'moment-timezone';

/**
 * Get the POEditor translation key for the given voucher name.
 * @param {string} name voucher type name
 * @returns {string} translation key.
 */
function voucherNameToKey(name) {
    return `common.voucher_types.${name.toLowerCase().replace(' ', '_')}`;
}

@Component({
    components: {
        SlideOver,
        InputText,
        InputTextArea,
        InputSelect,
        InputSelectList,
        InputDate,
        InputTime,
        InputTag,
        InputCheckbox,
        Avatar,
    },
})
export default class EventModal extends Vue {
    fields = {
        recurrence_rule: {
            frequency: 'WEEKLY',
            count: 4,
        },
        recurrenceUpdateMode: 'this',
        selectedVoucherType: 1,
        selectedClients: [],
        emails: [],
        phones: [],
        allSelectedClients: [],
        allGroups: [],
    };

    isRecurring = false;
    isLoading = false;
    showInviteFields = false;
    recurrenceMode = 'count';
    selectedDuration = 60;
    _mounted = false;

    bookingWarnings = [];

    search = null;
    // Used to sort the selected/registered clients first
    initallySelectedClients = [];
    // Used to cross cancelled registrations
    cancelledClients = {};

    @Prop() event;

    created() {
        this.isLoading = true;
        this.fetchClients().then(() => {
            this.fetchVouchers().then(() => {
                this.isLoading = false;
            });
        });

        this.fetchGroups();
    }

    mounted() {
        // Populate the modal on edit
        if (this.event) {
            this.fields = {
                ...this.fields,
                ...this.event,
                selectedVoucherType: this.event.voucher_type_id || 1,
                selectedClients:
                    this.event.event_registrations
                        ?.filter((registration) => !registration.cancelled_at)
                        ?.map((registration) => registration.client_id) || [],
            };

            this.initallySelectedClients = this.fields.selectedClients;

            this.cancelledClients = this.event.event_registrations?.reduce(
                (cancelledClients, registration) => {
                    if (registration.cancelled_at) {
                        cancelledClients[registration.client_id] = registration;
                    }
                    return cancelledClients;
                },
                {},
            );

            if (this.event.recurrence_rule) {
                this.isRecurring = true;
            }
        }

        this.$nextTick(() => {
            this._mounted = true;
        });
    }

    get isEdit() {
        return this.event?.id;
    }

    get voucherTypes() {
        return this.$store.state.vouchers.voucherTypes.filter((vt) => vt.max_event_regs < 10);
    }

    get voucherTypeSelectOptions() {
        return this.voucherTypes.map(({ id, type }) => ({
            label: this.$t(voucherNameToKey(type)),
            value: id,
        }));
    }

    /**
     * @returns {number} the total number of clients signed up or invited to the event.
     */
    get numInvitedAndRegisteredClients() {
        return (
            this.fields.emails.length +
            this.fields.phones.length +
            this.fields.allSelectedClients.length
        );
    }

    get selectedVoucherType() {
        return this.voucherTypes.find(({ id }) => id === this.fields.selectedVoucherType);
    }

    get clientLimit() {
        return this.selectedVoucherType.max_event_regs;
    }

    async fetchGroups() {
        await this.$store.dispatch(GROUPS_FETCH, { search: this.search });
        if (!this.search) {
            this.fields.allGroups = this.groups;
        }
    }

    get groups() {
        return this.$store.state.groups.groups;
    }

    /**
     * @returns {boolean} whether the client count exceeds the client limit
     */
    get isTooManyClients() {
        return this.numInvitedAndRegisteredClients > this.clientLimit;
    }

    async fetchClients() {
        await this.$store.dispatch(CLIENTS_FETCH, this.clientsQuery);
    }

    get fetchMoreClients() {
        return () => this.$store.dispatch(CLIENTS_FETCH_NEXT_PAGE, this.clientsQuery);
    }

    async fetchVouchers() {
        await this.$store.dispatch(CLIENTS_VOUCHERS_FETCH);
    }

    get clients() {
        if (!this.fields.selectedClients) return [];

        let currentClients = this.$store.getters.clients(this.clientsQuery);

        let refactoredGroups =
            this.groups
                ?.filter((item) => item.clients.length <= this.clientLimit)
                .map((item) => ({
                    ...item,
                    id: 'g' + item.id,
                })) || [];

        return [...refactoredGroups, ...currentClients];
    }

    get vouchers() {
        return this.$store.state.clients.vouchers;
    }

    vouchersForClient(clientId) {
        return this.vouchers[clientId]
            ? this.vouchers[clientId][this.fields.selectedVoucherType].totalCount
            : 0;
    }

    close() {
        this.$store.dispatch(CALENDAR_EVENTS_FETCH);
        this.$store.dispatch(CALENDAR_SLOTS_FETCH);
        this.$root.$emit('modal-close');
    }

    get invalid() {
        const {
            date,
            startTime,
            endTime,
            selectedVoucherType,
            emails,
            phones,
            allSelectedClients,
        } = this.fields;

        return (
            this.isTooManyClients ||
            !date ||
            !startTime ||
            !endTime ||
            !selectedVoucherType ||
            !(allSelectedClients.length || emails.length || phones.length || this.isEdit)
        );
    }

    applyDuration() {
        if (this.isEdit && !this._mounted) return;
        if (this.fields.startTime && this.selectedDuration) {
            this.fields.endTime = moment(this.fields.startTime).add(
                this.selectedDuration,
                'minutes',
            );
        }
    }

    onSearch() {
        this.fetchClients();
        this.fetchGroups();
    }

    async onSubmit() {
        if (this.invalid) return;
        if (this.isLoading) return;
        this.isLoading = true;
        const response = await this.$store.dispatch(REQUEST, {
            method: this.isEdit ? 'put' : 'post',
            url: this.isEdit ? `events/${this.event.id}` : 'events',
            data: {
                start_time: moment(this.fields.startTime)
                    .set({
                        year: this.fields.date.year(),
                        month: this.fields.date.month(),
                        date: this.fields.date.date(),
                        seconds: 0,
                    })
                    .utc(),
                end_time: moment(this.fields.endTime)
                    .set({
                        year: this.fields.date.year(),
                        month: this.fields.date.month(),
                        date: this.fields.date.date(),
                        seconds: 0,
                    })
                    .utc(),
                voucher_type_id: this.fields.selectedVoucherType,
                recurrence_rule: this.isRecurring
                    ? {
                          ...this.fields.recurrence_rule,
                          count:
                              this.recurrenceMode === 'count'
                                  ? this.fields.recurrence_rule.count
                                  : null,
                          until:
                              this.recurrenceMode === 'until'
                                  ? this.fields.recurrence_rule.until
                                  : null,
                      }
                    : null,
                update_mode: this.fields.recurrenceUpdateMode,
                client_ids: this.fields.allSelectedClients.length
                    ? this.fields.allSelectedClients
                    : this.fields.selectedClients,
                client_emails: this.fields.emails,
                client_phonenumbers: this.fields.phones,
                location: this.fields.location,
                notes: this.fields.notes,
                is_group_session: false,
            },
        });

        this.isLoading = false;

        if (response.status === 201) {
            this.$store.dispatch(VALIDATION_CREATE_MESSAGE, {
                type: 'success',
                message: 'professional.event_modal.create_success',
            });
            this.close();
        }
        if (response.status === 200) {
            this.$store.dispatch(VALIDATION_CREATE_MESSAGE, {
                type: 'success',
                message: 'professional.event_modal.update_success',
            });
            this.close();
        }
    }

    openEventDeleteDialog() {
        this.$root.$emit('modal-open', {
            modal: EventDeleteDialog,
            props: {
                event: this.event,
                callback: () => {
                    this.close();
                },
            },
        });
    }

    selectClient() {
        let selectedGroups = this.fields.selectedClients.filter((item) => typeof item === 'string');
        const clientIds = this.fields.selectedClients.filter((item) => typeof item !== 'string');

        if (selectedGroups) {
            selectedGroups = this.fields.allGroups.filter((g) =>
                selectedGroups.some((item) => 'g' + g.id === item),
            );
        }

        const groupClientIds = selectedGroups.flatMap((group) =>
            group.clients.map((client) => client.id),
        );

        this.fields.allSelectedClients = Array.from(new Set([...groupClientIds, ...clientIds]));
    }

    @Watch('fields.allSelectedClients')
    onSelectedClientsChange() {
        this.checkBookingAvailability();
    }

    async checkBookingAvailability() {
        const clientIds = this.fields.allSelectedClients;
        if (!clientIds.length) {
            this.bookingWarnings = [];
            return;
        }

        const weekStart = this.fields.date
            ? moment(this.fields.date).startOf('isoWeek').format('YYYY-MM-DD')
            : moment().startOf('isoWeek').format('YYYY-MM-DD');

        const warnings = [];
        const allClients = this.$store.getters.clients(this.clientsQuery);

        for (const clientId of clientIds) {
            const res = await this.$store.dispatch(CLIENT_BOOKING_AVAILABILITY, {
                clientId,
                weekStart,
            });
            if (res && res.status === 200 && res.data.data) {
                const data = res.data.data;
                const client = allClients.find((c) => c.id === clientId);
                const name = client ? client.full_name : `Client #${clientId}`;

                if (!data.has_subscription) {
                    continue;
                }

                if (data.events_per_week === null) {
                    warnings.push({
                        canBook: true,
                        message: `${name}: Unlimited plan`,
                    });
                } else if (data.events_used >= data.events_per_week) {
                    warnings.push({
                        canBook: false,
                        message: `${name}: Reached weekly limit (${data.events_used}/${data.events_per_week}) — ${data.subscription_name}`,
                    });
                } else {
                    warnings.push({
                        canBook: true,
                        message: `${name}: ${data.events_used}/${data.events_per_week} events this week (${data.subscription_name})`,
                    });
                }
            }
        }

        this.bookingWarnings = warnings;
    }

    get recurrenceUpdateModes() {
        return [
            { value: 'this', label: this.$t('professional.recurrence_update_modes.this') },
            { value: 'future', label: this.$t('professional.recurrence_update_modes.future') },
        ];
    }

    get clientsQuery() {
        return { search: this.search, event_id: this.event?.id };
    }
}
</script>
